/**
 * The filter bar's state, kept in the URL.
 *
 * It was component state, which meant it lasted exactly as long as the
 * component: switching from "Unassigned" to "All closed" remounted the list --
 * see the `key` on `ViewBody` -- and the filters you had just set went with it,
 * as did a reload and a link pasted to a colleague. Narrowing to a pipeline and
 * then checking the closed queue for the same pipeline is one question asked
 * twice, and the app made you re-answer the second half of it.
 *
 * The URL rather than localStorage, for the reason `TicketsView` already gives
 * about `?q=`: a filter is part of where you are, not something you did on the
 * way. Putting it there gets four things at once -- it survives the remount, it
 * survives a reload, back and forward step through it, and the address bar is a
 * shareable description of what you are looking at. A per-browser copy in
 * storage gets none of those and adds a second source of truth that the address
 * bar would then contradict.
 *
 * It is deliberately not the sort. That is a property of the table you are
 * reading rather than of the set of tickets, and nobody asked for it to follow
 * them across views.
 */

import {
  CHANNEL_FILTER_TYPES,
  type ChannelFilter,
  type Filters,
} from "../components/FilterBar";

/**
 * The parameters this module owns.
 *
 * Named once because two places need the same list: writing a change has to
 * clear the old value before setting the new, and a sidebar link has to carry
 * these and nothing else -- `?q=` is a search somebody typed, which is a
 * question about the whole desk rather than a way of looking at this view, and
 * dragging it into another view would silently re-run it there.
 */
const FILTER_PARAMS = ["pipeline", "assignee", "channel"] as const;

/**
 * One filter's value, or undefined when it is absent or nonsense.
 *
 * Anything unrecognised is dropped rather than refused. These arrive from an
 * address bar, so a truncated paste or a stale bookmark is ordinary input, and
 * the honest response to `?pipeline=old` is the unfiltered view rather than an
 * error about a query string.
 */
function readValue(raw: string | null): number | "unassigned" | undefined {
  if (raw === null) return undefined;
  if (raw === "unassigned") return "unassigned";
  return /^\d+$/.test(raw) ? Number(raw) : undefined;
}

/**
 * A channel filter: one channel by id, or every channel of a kind by its type.
 *
 * No "unassigned" here -- every ticket has a channel -- so this is not
 * `readValue`, which would let `?channel=unassigned` through to a request the
 * server refuses.
 */
function readChannel(raw: string | null): ChannelFilter | undefined {
  if (raw === null) return undefined;
  if ((CHANNEL_FILTER_TYPES as readonly string[]).includes(raw)) {
    return raw as ChannelFilter;
  }
  return /^\d+$/.test(raw) ? Number(raw) : undefined;
}

export function filtersFromParams(params: URLSearchParams): Filters {
  const filters: Filters = {};

  const pipeline = readValue(params.get("pipeline"));
  if (pipeline !== undefined) filters.pipeline = pipeline;

  const assignee = readValue(params.get("assignee"));
  if (assignee !== undefined) filters.assignee = assignee;

  const channel = readChannel(params.get("channel"));
  if (channel !== undefined) filters.channel = channel;

  return filters;
}

/**
 * The current query string with these filters applied, everything else left
 * alone.
 *
 * Takes the whole `URLSearchParams` and returns a new one so the caller can
 * hand it straight to `setSearchParams`, and so `?q=` and anything a later
 * feature adds survive a filter change untouched.
 */
export function withFilters(
  params: URLSearchParams,
  filters: Filters,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of FILTER_PARAMS) next.delete(key);

  if (filters.pipeline !== undefined) {
    next.set("pipeline", String(filters.pipeline));
  }
  if (filters.assignee !== undefined) {
    next.set("assignee", String(filters.assignee));
  }
  if (filters.channel !== undefined) {
    next.set("channel", String(filters.channel));
  }

  return next;
}

/**
 * Just the filters out of a query string, ready to append to a view's path.
 *
 * This is what makes the sidebar carry them: a `NavLink` to `/v/closed` is a
 * new location, and without this the filters are simply not in it. Returns "",
 * not "?", when there is nothing to carry, so the link is the bare path it was
 * before anybody touched a filter.
 */
export function filterSearch(search: string): string {
  const from = new URLSearchParams(search);
  const kept = new URLSearchParams();

  for (const key of FILTER_PARAMS) {
    const value = from.get(key);
    const valid =
      key === "channel" ? readChannel(value) : readValue(value);
    if (valid !== undefined) kept.set(key, value!);
  }

  const query = kept.toString();
  return query === "" ? "" : `?${query}`;
}
